import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { User, Briefcase, Check, X, AlertCircle, MapPin, Eye, EyeOff, Mail } from 'lucide-react';
import LocationMapPicker from '../components/LocationMapPicker';
import { SERVICE_CATEGORIES, SWISS_CANTONS } from '../constants/categories';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, API, language } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(location.state?.selectedRole || null);
  const [registrationStep, setRegistrationStep] = useState(1);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  
  // Address validation states
  const [addressValidation, setAddressValidation] = useState({
    isValidating: false,
    isValid: false,
    suggestions: [],
    error: null
  });
  
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role: '',
    language: language,
    address: '',
    // Professional fields
    categories: [],
    service_areas: { type: 'cantons', values: [] },
    work_location: { city: '', canton: '', country: 'Switzerland' },
    languages: ['en'],
    years_experience: '',
    // Map location data
    map_location: { lat: 46.8182, lng: 8.2275 },
    service_radius: 30,
  });
  
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Translations - MUST be defined before any functions that use 't'
  const content = {
    en: { 
      login: 'Login', 
      register: 'Register', 
      email: 'Email', 
      password: 'Password', 
      name: 'Full Name', 
      phone: 'Phone (optional)', 
      selectRole: 'Select your role', 
      client: 'I need services', 
      professional: 'I provide services', 
      loginBtn: 'Sign In', 
      registerBtn: 'Create Account',
      welcome: 'Welcome',
      signInOrCreate: 'Sign in or create an account',
      forgotPassword: 'Forgot your password?',
      resetPassword: 'Reset Password',
      resetPasswordDesc: 'Enter your email and we\'ll send you a temporary password',
      sendTempPassword: 'Send Temporary Password',
      backToLogin: '← Back to Login',
      yourEmail: 'your@email.com',
      acceptTerms: 'Please accept Terms & Policy',
      registrationSuccess: 'Registration successful! Welcome to Helply!',
      registrationFailed: 'Registration failed',
      fullAddress: 'Full Address *',
      addressPlaceholder: 'Street, Number, City, Postal Code',
      addressVerified: 'Address verified! Suggestions:',
      enterAddress: 'Enter your complete address. We\'ll verify it automatically.',
      addressNotFound: 'Address not found. Please check and try again.',
      couldNotValidate: 'Could not validate address',
      loginSuccess: 'Login successful!',
      loginFailed: 'Login failed',
      enterEmail: 'Please enter your email',
      tempPasswordSent: 'Temporary password sent to your email!',
      failedToSendPassword: 'Failed to send temporary password',
      selectRoleError: 'Please select a role',
      termsAccept: 'I have read and accept the ',
      termsLink: 'terms and conditions',
      termsAcceptEnd: '',
      nextYourDetails: 'Next: Your Details',
      selectCategories: 'Select service categories',
      minOneCategory: '(minimum 1)',
      completeProfile: 'Complete your profile',
      selectAtLeastOne: 'Select at least one category',
      selectUpTo: 'Select up to 5',
      serviceAreas: 'Service Areas (Cantons) *',
      selectCantons: 'Select cantons where you provide services',
      professionalDetails: 'Professional Details',
      yourServiceNeeds: 'Your Service Needs',
      serviceCategories: 'Service Categories *',
      servicesYouNeed: 'Services You Need *',
      selectAllServices: 'Select all services you provide',
      selectServicesNeed: 'Select services you typically need',
      back: 'Back',
      locationRequired: 'Location is required',
      registerComplete: 'Complete Registration'
    },
    de: { 
      login: 'Anmelden', 
      register: 'Registrieren', 
      email: 'E-Mail', 
      password: 'Passwort', 
      name: 'Vollständiger Name', 
      phone: 'Telefon (optional)', 
      selectRole: 'Wählen Sie Ihre Rolle', 
      client: 'Ich brauche Dienstleistungen', 
      professional: 'Ich biete Dienstleistungen an', 
      loginBtn: 'Anmelden', 
      registerBtn: 'Konto erstellen',
      welcome: 'Willkommen',
      signInOrCreate: 'Melden Sie sich an oder erstellen Sie ein Konto',
      forgotPassword: 'Passwort vergessen?',
      resetPassword: 'Passwort zurücksetzen',
      resetPasswordDesc: 'Geben Sie Ihre E-Mail ein und wir senden Ihnen ein temporäres Passwort',
      sendTempPassword: 'Temporäres Passwort senden',
      backToLogin: '← Zurück zur Anmeldung',
      yourEmail: 'ihre@email.com',
      acceptTerms: 'Bitte akzeptieren Sie die Nutzungsbedingungen',
      registrationSuccess: 'Registrierung erfolgreich! Willkommen bei Helply!',
      registrationFailed: 'Registrierung fehlgeschlagen',
      fullAddress: 'Vollständige Adresse *',
      addressPlaceholder: 'Straße, Nummer, Stadt, Postleitzahl',
      addressVerified: 'Adresse verifiziert! Vorschläge:',
      enterAddress: 'Geben Sie Ihre vollständige Adresse ein. Wir überprüfen sie automatisch.',
      addressNotFound: 'Adresse nicht gefunden. Bitte überprüfen und erneut versuchen.',
      couldNotValidate: 'Adresse konnte nicht validiert werden',
      loginSuccess: 'Anmeldung erfolgreich!',
      loginFailed: 'Anmeldung fehlgeschlagen',
      enterEmail: 'Bitte geben Sie Ihre E-Mail ein',
      tempPasswordSent: 'Temporäres Passwort an Ihre E-Mail gesendet!',
      failedToSendPassword: 'Konnte temporäres Passwort nicht senden',
      selectRoleError: 'Bitte wählen Sie eine Rolle',
      termsAccept: 'Ich habe die ',
      termsLink: 'AGB',
      termsAcceptEnd: ' gelesen und akzeptiere sie',
      nextYourDetails: 'Weiter: Ihre Details',
      selectCategories: 'Servicekategorien auswählen',
      minOneCategory: '(mindestens 1)',
      completeProfile: 'Vervollständigen Sie Ihr Profil',
      selectAtLeastOne: 'Mindestens eine Kategorie auswählen',
      selectUpTo: 'Bis zu 5 auswählen',
      serviceAreas: 'Servicegebiete (Kantone) *',
      selectCantons: 'Wählen Sie Kantone aus, in denen Sie Dienste anbieten',
      professionalDetails: 'Professionelle Details',
      yourServiceNeeds: 'Ihre Dienstleistungsbedürfnisse',
      serviceCategories: 'Dienstleistungskategorien *',
      servicesYouNeed: 'Benötigte Dienstleistungen *',
      selectAllServices: 'Wählen Sie alle Dienste aus, die Sie anbieten',
      selectServicesNeed: 'Wählen Sie Dienste aus, die Sie benötigen',
      back: 'Zurück',
      locationRequired: 'Standort ist erforderlich',
      registerComplete: 'Registrierung abschließen'
    },
    it: { 
      login: 'Accedi', 
      register: 'Registrati', 
      email: 'Email', 
      password: 'Password', 
      name: 'Nome completo', 
      phone: 'Telefono (opzionale)', 
      selectRole: 'Seleziona il tuo ruolo', 
      client: 'Ho bisogno di servizi', 
      professional: 'Fornisco servizi', 
      loginBtn: 'Accedi', 
      registerBtn: 'Crea account',
      welcome: 'Benvenuto',
      signInOrCreate: 'Accedi o crea un account',
      forgotPassword: 'Password dimenticata?',
      resetPassword: 'Reimposta Password',
      resetPasswordDesc: 'Inserisci la tua email e ti invieremo una password temporanea',
      sendTempPassword: 'Invia Password Temporanea',
      backToLogin: '← Torna al Login',
      yourEmail: 'tua@email.com',
      acceptTerms: 'Per favore accetta i Termini e Condizioni',
      registrationSuccess: 'Registrazione completata! Benvenuto su Helply!',
      registrationFailed: 'Registrazione fallita',
      fullAddress: 'Indirizzo completo *',
      addressPlaceholder: 'Via, Numero, Città, CAP',
      addressVerified: 'Indirizzo verificato! Suggerimenti:',
      enterAddress: 'Inserisci il tuo indirizzo completo. Lo verificheremo automaticamente.',
      addressNotFound: 'Indirizzo non trovato. Controlla e riprova.',
      couldNotValidate: 'Impossibile validare l\'indirizzo',
      loginSuccess: 'Accesso riuscito!',
      loginFailed: 'Accesso fallito',
      enterEmail: 'Per favore inserisci la tua email',
      tempPasswordSent: 'Password temporanea inviata alla tua email!',
      failedToSendPassword: 'Impossibile inviare la password temporanea',
      selectRoleError: 'Per favore seleziona un ruolo',
      termsAccept: 'Ho letto e accetto i ',
      termsLink: 'termini e condizioni',
      termsAcceptEnd: '',
      nextYourDetails: 'Avanti: I tuoi dati',
      selectCategories: 'Seleziona categorie di servizio',
      minOneCategory: '(minimo 1)',
      completeProfile: 'Completa il tuo profilo',
      selectAtLeastOne: 'Seleziona almeno una categoria',
      selectUpTo: 'Seleziona fino a 5',
      serviceAreas: 'Zone di servizio (Cantoni) *',
      selectCantons: 'Seleziona i cantoni in cui offri servizi',
      professionalDetails: 'Dettagli professionali',
      yourServiceNeeds: 'Le tue esigenze di servizio',
      serviceCategories: 'Categorie di servizio *',
      servicesYouNeed: 'Servizi di cui hai bisogno *',
      selectAllServices: 'Seleziona tutti i servizi che offri',
      selectServicesNeed: 'Seleziona i servizi di cui hai tipicamente bisogno',
      back: 'Indietro',
      locationRequired: 'La posizione è richiesta',
      registerComplete: 'Completa registrazione'
    },
    fr: { 
      login: 'Connexion', 
      register: 'S\'inscrire', 
      email: 'Email', 
      password: 'Mot de passe', 
      name: 'Nom complet', 
      phone: 'Téléphone (optionnel)', 
      selectRole: 'Sélectionnez votre rôle', 
      client: 'J\'ai besoin de services', 
      professional: 'Je fournis des services', 
      loginBtn: 'Se connecter', 
      registerBtn: 'Créer un compte',
      welcome: 'Bienvenue',
      signInOrCreate: 'Connectez-vous ou créez un compte',
      forgotPassword: 'Mot de passe oublié ?',
      resetPassword: 'Réinitialiser le mot de passe',
      resetPasswordDesc: 'Entrez votre email et nous vous enverrons un mot de passe temporaire',
      sendTempPassword: 'Envoyer mot de passe temporaire',
      backToLogin: '← Retour à la connexion',
      yourEmail: 'votre@email.com',
      acceptTerms: 'Veuillez accepter les conditions générales',
      registrationSuccess: 'Inscription réussie ! Bienvenue sur Helply !',
      registrationFailed: 'Échec de l\'inscription',
      fullAddress: 'Adresse complète *',
      addressPlaceholder: 'Rue, Numéro, Ville, Code postal',
      addressVerified: 'Adresse vérifiée ! Suggestions :',
      enterAddress: 'Entrez votre adresse complète. Nous la vérifierons automatiquement.',
      addressNotFound: 'Adresse introuvable. Veuillez vérifier et réessayer.',
      couldNotValidate: 'Impossible de valider l\'adresse',
      loginSuccess: 'Connexion réussie !',
      loginFailed: 'Échec de la connexion',
      enterEmail: 'Veuillez entrer votre email',
      tempPasswordSent: 'Mot de passe temporaire envoyé à votre email !',
      failedToSendPassword: 'Impossible d\'envoyer le mot de passe temporaire',
      selectRoleError: 'Veuillez sélectionner un rôle',
      termsAccept: 'J\'ai lu et j\'accepte les ',
      termsLink: 'conditions générales',
      termsAcceptEnd: '',
      nextYourDetails: 'Suivant : Vos informations',
      selectCategories: 'Sélectionner les catégories de services',
      minOneCategory: '(minimum 1)',
      completeProfile: 'Complétez votre profil',
      selectAtLeastOne: 'Sélectionnez au moins une catégorie',
      selectUpTo: 'Sélectionnez jusqu\'à 5',
      serviceAreas: 'Zones de service (Cantons) *',
      selectCantons: 'Sélectionnez les cantons où vous proposez vos services',
      professionalDetails: 'Détails professionnels',
      yourServiceNeeds: 'Vos besoins en services',
      serviceCategories: 'Catégories de services *',
      servicesYouNeed: 'Services dont vous avez besoin *',
      selectAllServices: 'Sélectionnez tous les services que vous proposez',
      selectServicesNeed: 'Sélectionnez les services dont vous avez généralement besoin',
      back: 'Retour',
      locationRequired: 'La localisation est requise',
      registerComplete: 'Terminer l\'inscription'
    }
  };

  const t = content[language] || content.en;

  // Address validation with debounce
  useEffect(() => {
    const validateAddress = async () => {
      if (!registerData.address || registerData.address.length < 5) {
        setAddressValidation({ isValidating: false, isValid: false, suggestions: [], error: null });
        return;
      }

      setAddressValidation(prev => ({ ...prev, isValidating: true, error: null }));

      try {
        const response = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: {
            q: registerData.address,
            format: 'json',
            addressdetails: 1,
            limit: 5
          },
          headers: {
            'Accept-Language': language === 'de' ? 'de' : 'en'
          }
        });

        if (response.data && response.data.length > 0) {
          const suggestions = response.data.map(item => ({
            display_name: item.display_name,
            lat: item.lat,
            lon: item.lon,
            address: item.address
          }));
          
          setAddressValidation({
            isValidating: false,
            isValid: true,
            suggestions: suggestions,
            error: null
          });
        } else {
          setAddressValidation({
            isValidating: false,
            isValid: false,
            suggestions: [],
            error: t.addressNotFound || 'Address not found. Please check and try again.'
          });
        }
      } catch (error) {
        console.error('Address validation error:', error);
        setAddressValidation({
          isValidating: false,
          isValid: false,
          suggestions: [],
          error: t.couldNotValidate || 'Could not validate address'
        });
      }
    };

    const timeoutId = setTimeout(() => {
      validateAddress();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [registerData.address, language]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      await login(response.data.token, response.data.user, response.data.firebase_token);
      toast.success(t.loginSuccess || 'Login successful!');
      const requestedPath = location.state?.returnTo;
      const safeReturnPath = typeof requestedPath === 'string'
        && requestedPath.startsWith('/')
        && !requestedPath.startsWith('//')
        && !requestedPath.includes('\\')
        && !requestedPath.startsWith('/auth')
        ? requestedPath
        : '/';
      navigate(safeReturnPath, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t.loginFailed || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error(t.enterEmail || 'Please enter your email');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success(response.data.message || t.tempPasswordSent || 'Temporary password sent to your email!');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || t.failedToSendPassword || 'Failed to send temporary password');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      toast.error(t.selectRoleError || 'Please select a role');
      return;
    }

    if (!acceptedTerms) {
      toast.error(t.acceptTerms || 'Please accept Terms & Policy');
      return;
    }

    // Step 1: Basic info completed, go to step 2 (categories)
    if (registrationStep === 1) {
      setRegistrationStep(2);
      return;
    }

    // Step 2: Categories completed, submit registration
    if (registrationStep === 2) {
      setLoading(true);
      try {
        const payload = { 
          ...registerData, 
          role: selectedRole,
          years_experience: registerData.years_experience ? parseInt(registerData.years_experience) : null
        };
        
        const response = await axios.post(`${API}/auth/register`, payload);
        await login(response.data.token, response.data.user, response.data.firebase_token);
        toast.success(t.registrationSuccess || 'Registration successful! Welcome to Helply!');
        
        // Navigate to appropriate dashboard
        if (selectedRole === 'client') {
          navigate('/dashboard/client');
        } else {
          navigate('/dashboard/professional');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || t.registrationFailed || 'Registration failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleLanguage = (lang) => {
    const langs = registerData.languages;
    if (langs.includes(lang)) {
      setRegisterData({ 
        ...registerData, 
        languages: langs.filter(l => l !== lang).length > 0 ? langs.filter(l => l !== lang) : langs 
      });
    } else {
      setRegisterData({ ...registerData, languages: [...langs, lang] });
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/helply-logo.jpg" alt="Helply" className="w-16 h-16 rounded-xl mx-auto mb-3" />
        </div>

        <Card className="swiss-shadow-lg border-0">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">{t.welcome || 'Welcome'}</CardTitle>
            <CardDescription>{t.signInOrCreate || 'Sign in or create an account'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" data-testid="auth-tabs">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t.login}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t.register}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {!showForgotPassword ? (
                  <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                    <div>
                      <Label htmlFor="login-email">{t.email}</Label>
                      <Input
                        id="login-email"
                        type="email"
                        required
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        data-testid="login-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">{t.password}</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showLoginPassword ? "text" : "password"}
                          required
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          data-testid="login-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                          data-testid="login-password-visibility-button"
                        >
                          {showLoginPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="login-submit-btn">
                      {loading ? '...' : t.loginBtn}
                    </Button>
                    
                    {/* Forgot Password Link */}
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid="forgot-password-button"
                      >
                        {t.forgotPassword || 'Forgot your password?'}
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Forgot Password Form */
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="text-center mb-4">
                      <Mail className="w-12 h-12 mx-auto text-blue-600 mb-2" />
                      <h3 className="font-semibold text-lg">
                        {t.resetPassword || 'Reset Password'}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        {t.resetPasswordDesc || 'Enter your email and we\'ll send you a temporary password'}
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="forgot-email">{t.email}</Label>
                      <Input
                        id="forgot-email"
                        type="email"
                        required
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        placeholder={t.yourEmail || 'your@email.com'}
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700" 
                      disabled={forgotPasswordLoading}
                    >
                      {forgotPasswordLoading ? '...' : (t.sendTempPassword || 'Send Temporary Password')}
                    </Button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                      }}
                      className="w-full text-sm text-gray-600 hover:text-gray-800"
                    >
                      {t.backToLogin || '← Back to Login'}
                    </button>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  {/* STEP 1: Basic Information */}
                  {registrationStep === 1 && (
                    <>
                      <div>
                        <Label htmlFor="register-name">{t.name}</Label>
                        <Input
                          id="register-name"
                          required
                          value={registerData.name}
                          onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                          data-testid="register-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-email">{t.email}</Label>
                        <Input
                          id="register-email"
                          type="email"
                          required
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          data-testid="register-email-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-phone">{t.phone}</Label>
                        <Input
                          id="register-phone"
                          type="tel"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          data-testid="register-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="register-address">
                          {t.fullAddress || 'Full Address *'}
                        </Label>
                        <div className="relative">
                          <Input
                            id="register-address"
                            type="text"
                            required
                            value={registerData.address}
                            onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                            placeholder={t.addressPlaceholder || 'Street, Number, City, Postal Code'}
                            data-testid="register-address-input"
                            className={`pr-10 ${addressValidation.isValid ? 'border-green-500' : addressValidation.error ? 'border-red-500' : ''}`}
                          />
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            {addressValidation.isValidating && (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                            )}
                            {!addressValidation.isValidating && addressValidation.isValid && (
                              <Check className="w-5 h-5 text-green-600" />
                            )}
                            {!addressValidation.isValidating && addressValidation.error && (
                              <X className="w-5 h-5 text-red-600" />
                            )}
                          </div>
                        </div>
                        
                        {addressValidation.error && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {addressValidation.error}
                          </p>
                        )}
                        
                        {addressValidation.isValid && addressValidation.suggestions.length > 0 && (
                          <div className="mt-2 border border-green-200 rounded-lg p-2 bg-green-50">
                            <p className="text-xs text-green-800 font-medium mb-2 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              {t.addressVerified || 'Address verified! Suggestions:'}
                            </p>
                            <div className="space-y-1 max-h-32 overflow-y-auto">
                              {addressValidation.suggestions.slice(0, 3).map((suggestion, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => setRegisterData({ ...registerData, address: suggestion.display_name })}
                                  className="w-full text-left text-xs p-2 hover:bg-green-100 rounded border border-green-200 flex items-start gap-2"
                                >
                                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-green-600" />
                                  <span className="text-gray-700">{suggestion.display_name}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <p className="text-xs text-gray-500 mt-1">
                          {t.enterAddress || 'Enter your complete address. We\'ll verify it automatically.'}
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="register-password">{t.password}</Label>
                        <div className="relative">
                          <Input
                            id="register-password"
                            type={showRegisterPassword ? "text" : "password"}
                            required
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            data-testid="register-password-input"
                            className="pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                          >
                            {showRegisterPassword ? (
                              <EyeOff className="w-5 h-5" />
                            ) : (
                              <Eye className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label>{t.selectRole}</Label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setSelectedRole('client')}
                            className={`p-4 border-2 rounded-lg text-center hover:border-blue-600 transition-colors ${
                              selectedRole === 'client' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'
                            }`}
                            data-testid="role-client-btn"
                          >
                            <User className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                            <p className="font-medium text-sm">{t.client}</p>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedRole('professional')}
                            className={`p-4 border-2 rounded-lg text-center hover:border-red-600 transition-colors ${
                              selectedRole === 'professional' ? 'border-red-600 bg-red-50' : 'border-gray-200'
                            }`}
                            data-testid="role-professional-btn"
                          >
                            <Briefcase className="w-8 h-8 mx-auto mb-2 text-red-600" />
                            <p className="font-medium text-sm">{t.professional}</p>
                          </button>
                        </div>
                      </div>

                      {/* Location Selection for Both Clients and Professionals */}
                      {selectedRole && (
                        <div className="border-t pt-4">
                          <LocationMapPicker
                            location={registerData.map_location}
                            radius={registerData.service_radius}
                            onLocationChange={(location) => setRegisterData({ ...registerData, map_location: location })}
                            onRadiusChange={(radius) => setRegisterData({ ...registerData, service_radius: radius })}
                            language={language}
                          />
                        </div>
                      )}

                      <div className="flex items-start space-x-2">
                        <input
                          type="checkbox"
                          id="terms"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="mt-1"
                          data-testid="terms-checkbox"
                        />
                        <label htmlFor="terms" className="text-sm text-gray-600">
                          {t.termsAccept || 'I have read and accept the '}
                          <a href="/terms-policy" target="_blank" className="text-blue-600 underline">
                            {t.termsLink || 'terms and conditions'}
                          </a>
                          {t.termsAcceptEnd || ''}
                        </label>
                      </div>

                      <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading || !acceptedTerms} data-testid="register-submit-btn">
                        {loading ? '...' : (t.nextYourDetails || 'Next: Your Details')}
                      </Button>
                    </>
                  )}

                  {/* STEP 2: Categories & Service Areas (only for professionals and clients) */}
                  {registrationStep === 2 && (
                    <>
                      <div className="mb-4">
                        <h3 className="font-bold text-lg mb-1">
                          {selectedRole === 'professional' ? (t.professionalDetails || 'Professional Details') : (t.yourServiceNeeds || 'Your Service Needs')}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t.completeProfile || 'Complete your profile'}
                        </p>
                      </div>

                      {/* Service Categories */}
                      <div>
                        <Label>
                          {selectedRole === 'professional' ? (t.serviceCategories || 'Service Categories *') : (t.servicesYouNeed || 'Services You Need *')}
                        </Label>
                        <p className="text-xs text-gray-500 mb-2">
                          {selectedRole === 'professional' ? (t.selectAllServices || 'Select all services you provide') : (t.selectServicesNeed || 'Select services you typically need')}
                        </p>
                        <div className="max-h-96 overflow-y-auto pr-2">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                            {SERVICE_CATEGORIES.map((cat) => {
                              const shortLabel = (language === 'en' ? cat.en : language === 'it' ? cat.it : language === 'fr' ? cat.fr : cat.de).split('(')[0].trim();
                              return (
                              <button
                                key={cat.value}
                                type="button"
                                onClick={() => {
                                  const cats = registerData.categories;
                                  const newCats = cats.includes(cat.value)
                                    ? cats.filter(c => c !== cat.value)
                                    : [...cats, cat.value];
                                  setRegisterData({ ...registerData, categories: newCats });
                                }}
                                className={`px-3 py-2.5 rounded-lg text-xs font-medium border-2 transition-all duration-200 ${
                                  registerData.categories.includes(cat.value)
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                                data-testid={`category-${cat.value}-btn`}
                              >
                                {shortLabel}
                              </button>
                            )})}
                          </div>
                        </div>
                      </div>

                      {/* Service Areas */}
                      <div>
                        <Label>{language === 'en' ? 'Service Areas (Cantons) *' : language === 'it' ? 'Aree di Servizio (Cantoni) *' : language === 'fr' ? 'Zones de Service (Cantons) *' : 'Servicegebiete (Kantone) *'}</Label>
                        <p className="text-xs text-gray-500 mb-2">
                          {language === 'en' ? 'Select cantons where you provide services' : language === 'it' ? 'Seleziona i cantoni dove offri servizi' : language === 'fr' ? 'Sélectionnez les cantons où vous fournissez des services' : 'Wählen Sie Kantone aus, in denen Sie Dienste anbieten'}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded p-2">
                          {SWISS_CANTONS.map((canton) => (
                            <button
                              key={canton.value}
                              type="button"
                              onClick={() => {
                                const areas = registerData.service_areas.values;
                                const newAreas = areas.includes(canton.value)
                                  ? areas.filter(a => a !== canton.value)
                                  : [...areas, canton.value];
                                setRegisterData({ 
                                  ...registerData, 
                                  service_areas: { type: 'cantons', values: newAreas }
                                });
                              }}
                              className={`px-2 py-1 rounded text-xs border transition-colors ${
                                registerData.service_areas.values.includes(canton.value)
                                  ? 'bg-green-600 text-white border-green-600'
                                  : 'bg-white text-gray-600 border-gray-300'
                              }`}
                              data-testid={`canton-${canton.value}-btn`}
                            >
                              {language === 'en' ? canton.en : language === 'it' ? canton.it : language === 'fr' ? canton.fr : canton.de}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Work Location */}
                      <div>
                        <Label>{language === 'en' ? 'Work Location' : language === 'it' ? 'Sede di Lavoro' : language === 'fr' ? 'Lieu de Travail' : 'Arbeitsstandort'}</Label>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            placeholder={language === 'en' ? 'City' : language === 'it' ? 'Città' : language === 'fr' ? 'Ville' : 'Stadt'}
                            value={registerData.work_location.city}
                            onChange={(e) => setRegisterData({ 
                              ...registerData, 
                              work_location: { ...registerData.work_location, city: e.target.value }
                            })}
                            data-testid="work-city-input"
                          />
                          <Input
                            placeholder={language === 'en' ? 'Canton' : language === 'it' ? 'Cantone' : language === 'fr' ? 'Canton' : 'Kanton'}
                            value={registerData.work_location.canton}
                            onChange={(e) => setRegisterData({ 
                              ...registerData, 
                              work_location: { ...registerData.work_location, canton: e.target.value }
                            })}
                            data-testid="work-canton-input"
                          />
                        </div>
                      </div>

                      {/* Map Location Picker */}
                      <div className="border-t pt-4">
                        <LocationMapPicker
                          location={registerData.map_location}
                          radius={registerData.service_radius}
                          onLocationChange={(location) => setRegisterData({ ...registerData, map_location: location })}
                          onRadiusChange={(radius) => setRegisterData({ ...registerData, service_radius: radius })}
                          language={language}
                        />
                      </div>

                      {/* Languages */}
                      <div>
                        <Label>{language === 'en' ? 'Languages Spoken' : language === 'it' ? 'Lingue Parlate' : language === 'fr' ? 'Langues Parlées' : 'Gesprochene Sprachen'}</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {['en', 'de', 'fr', 'it'].map(lang => (
                            <button
                              key={lang}
                              type="button"
                              onClick={() => toggleLanguage(lang)}
                              className={`px-3 py-1 rounded border transition-colors ${
                                registerData.languages.includes(lang)
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300'
                              }`}
                            >
                              {lang.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Years of Experience (Professional only) */}
                      {selectedRole === 'professional' && (
                        <div>
                          <Label htmlFor="years-exp">{language === 'en' ? 'Years of Experience' : language === 'it' ? 'Anni di Esperienza' : language === 'fr' ? 'Années d\'Expérience' : 'Jahre Erfahrung'}</Label>
                          <Input
                            id="years-exp"
                            type="number"
                            min="0"
                            value={registerData.years_experience}
                            onChange={(e) => setRegisterData({ ...registerData, years_experience: e.target.value })}
                            data-testid="years-experience-input"
                          />
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={() => setRegistrationStep(1)}
                          className="flex-1"
                          disabled={loading}
                        >
                          {language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : language === 'fr' ? 'Retour' : 'Zurück'}
                        </Button>
                        <Button 
                          type="submit" 
                          className="flex-1 bg-green-600 hover:bg-green-700" 
                          disabled={loading}
                          data-testid="complete-registration-btn"
                        >
                          {loading ? '...' : (language === 'en' ? 'Complete Registration' : language === 'it' ? 'Completa Registrazione' : language === 'fr' ? 'Terminer l\'inscription' : 'Registrierung abschließen')}
                        </Button>
                      </div>
                    </>
                  )}
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
// Updated Mon Jan  5 11:24:29 UTC 2026
